// OPERATOR-CONSOLE: Settings › Operator agents — the Deployer, the Fixer and the Builder (System
// agents: never DE or AG), each with its model setting, sealed API key (only its last four shown),
// allowed APIs, daily cap and job tokens (shown once); then the jobs they run, each a checklist,
// with Retry on a failed one, and "New company from pack".
import { Currency } from "@companyos/schema/contracts";
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  Notice,
  QuickAddDialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BanIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  CpuIcon,
  KeyRoundIcon,
  ListChecksIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  RotateCwIcon,
  ShieldCheckIcon,
  WalletIcon,
} from "lucide-react";
import { Fragment, useId, useState } from "react";
import type { Api } from "../../../api/client";
import { dollars } from "../../billing/billing-money";
import { formatDate, formatDateTime } from "../../format";
import { problemText } from "../../ops-parts";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import { CheckField, KvList, MoneyField, NumberField, SelectField, TextField } from "./fields";
import { useSettingsScope } from "./setting-card";

type AgentList = OperationOutput<"operator.agents.list">;
type Agent = AgentList["agents"][number];
type OpApi = Agent["allowed_apis"][number];
export type Job = OperationOutput<"operator.jobs.get">;

export const agentsKey = ["operator", "agents"] as const;
const agentsQuery = (api: Api) =>
  queryOptions({ queryKey: agentsKey, queryFn: () => api.client.operator.agents.list() });
export const jobsKey = ["operator", "jobs"] as const;
const jobsQuery = (api: Api) =>
  queryOptions({ queryKey: jobsKey, queryFn: () => api.client.operator.jobs.list() });

const API_LABEL: Record<OpApi, string> = {
  "companies.from_pack": "New company from pack",
  "baseline.apply": "Apply baseline",
  "jobs.read": "Read jobs",
  "jobs.report": "Report job steps",
};
const KIND_LABEL: Record<Agent["model_kind"], string> = {
  gateway: "Gateway",
  anthropic_api: "Anthropic API",
  subscription: "Subscription",
};

export function OperatorAgents({ api }: { api: Api }) {
  const q = useQuery(agentsQuery(api));
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="operator-agents">
      {q.isPending ? (
        <Loading label="Loading operator agents" rows={3} />
      ) : q.isError ? (
        <LoadError what="Operator agents" error={q.error} />
      ) : (
        <div className="grid gap-3 xl:grid-cols-3">
          {q.data.agents.map((a) => (
            <AgentCard key={a.id} api={api} agent={a} apis={q.data.apis} />
          ))}
        </div>
      )}
      <Jobs api={api} />
    </div>
  );
}

function useAgentSaved() {
  const qc = useQueryClient();
  return (next: Agent) =>
    qc.setQueryData<AgentList>(agentsKey, (old) =>
      old ? { ...old, agents: old.agents.map((a) => (a.id === next.id ? next : a)) } : old,
    );
}

type Modal = "model" | "key" | "apis" | "cap" | "token" | null;

function AgentCard({ api, agent: a, apis }: { api: Api; agent: Agent; apis: readonly OpApi[] }) {
  const [modal, setModal] = useState<Modal>(null);
  const saved = useAgentSaved();
  const status = useMutation({
    mutationFn: () =>
      api.client.operator.agents.put({
        params: { slug: a.slug },
        body: { status: a.status === "active" ? "paused" : "active" },
      }),
    onSuccess: saved,
  });
  const close = () => setModal(null);
  return (
    <section
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-slot="operator-agent"
      data-agent={a.slug}
      aria-label={a.name}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <CpuIcon className="size-4 text-muted-foreground" aria-hidden />
          {a.name}
        </h3>
        <Badge variant="neutral">System</Badge>
        <InfoTip label={`About the ${a.name}`}>{a.purpose}</InfoTip>
        <Badge
          variant={a.status === "active" ? "success" : "outline"}
          className="ml-auto"
          data-slot="agent-status"
        >
          {a.status === "active" ? "Active" : "Paused"}
        </Badge>
      </div>
      <KvList
        rows={[
          {
            key: "model",
            label: "Model",
            value: (
              <span className="break-all">
                {KIND_LABEL[a.model_kind]} ·{" "}
                <code>{a.model_setting.split(":").slice(1).join(":")}</code>
              </span>
            ),
          },
          {
            key: "key",
            label: "API key",
            value: a.has_key ? `Key ending ••••${a.key_hint ?? ""}` : "None",
          },
          {
            key: "apis",
            label: "APIs",
            value:
              a.allowed_apis.length === 0
                ? "None"
                : a.allowed_apis.map((x) => API_LABEL[x]).join(", "),
          },
          { key: "cap", label: "Daily cap", value: dollars(a.daily_cap_micros) },
          { key: "spent", label: "Spent today", value: dollars(a.spent_today_micros) },
        ]}
      />
      <div className="flex flex-wrap gap-1">
        <Button size="xs" variant="secondary" onClick={() => setModal("model")}>
          <CpuIcon aria-hidden /> Model
        </Button>
        <Button size="xs" variant="secondary" onClick={() => setModal("key")}>
          <KeyRoundIcon aria-hidden /> API key
        </Button>
        <Button size="xs" variant="secondary" onClick={() => setModal("apis")}>
          <ShieldCheckIcon aria-hidden /> APIs
        </Button>
        <Button size="xs" variant="secondary" onClick={() => setModal("cap")}>
          <WalletIcon aria-hidden /> Cap
        </Button>
        <Button
          size="xs"
          variant="ghost"
          disabled={status.isPending}
          onClick={() => status.mutate()}
        >
          {a.status === "active" ? <PauseIcon aria-hidden /> : <PlayIcon aria-hidden />}
          {a.status === "active" ? "Pause" : "Resume"}
        </Button>
      </div>
      {status.isError ? (
        <FieldError>{problemText(status.error, "The status was not changed.")}</FieldError>
      ) : null}
      <Tokens api={api} agent={a} onIssue={() => setModal("token")} />
      {modal === "model" ? <ModelDialog api={api} agent={a} onClose={close} /> : null}
      {modal === "key" ? <KeyDialog api={api} agent={a} onClose={close} /> : null}
      {modal === "apis" ? <ApisDialog api={api} agent={a} apis={apis} onClose={close} /> : null}
      {modal === "cap" ? <CapDialog api={api} agent={a} onClose={close} /> : null}
      {modal === "token" ? <TokenDialog api={api} agent={a} apis={apis} onClose={close} /> : null}
    </section>
  );
}

const MODEL_SETTING = /^(gateway|anthropic_api|subscription):[A-Za-z0-9._/:@-]{1,120}$/;

function ModelDialog({ api, agent: a, onClose }: { api: Api; agent: Agent; onClose: () => void }) {
  const saved = useAgentSaved();
  const [kind, setKind] = useState<Agent["model_kind"]>(a.model_kind);
  const [value, setValue] = useState<string | null>(a.model_setting.split(":").slice(1).join(":"));
  const setting = `${kind}:${(value ?? "").trim()}`;
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.agents.put({
        params: { slug: a.slug },
        body: { model_setting: setting },
      }),
    onSuccess: (next) => {
      saved(next);
      onClose();
    },
  });
  const kinds = (["gateway", "anthropic_api", "subscription"] as const).filter(
    (k) => k !== "subscription" || a.slug === "builder",
  );
  return (
    <QuickAddDialog
      title={`${a.name} model`}
      info="Gateway: a model alias on our gateway. Anthropic API: the key sealed under API key. Subscription: a runner on a subscription, for operator work only (the Builder)."
      submitLabel="Save"
      pending={save.isPending}
      canSubmit={MODEL_SETTING.test(setting)}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={save.error ? <FieldError>{problemText(save.error, "Not saved.")}</FieldError> : null}
      slot="agent-model-dialog"
    >
      <SelectField
        label="Kind"
        value={kind}
        options={kinds.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
        onChange={setKind}
      />
      <TextField
        label={kind === "gateway" ? "Model alias" : kind === "anthropic_api" ? "Model" : "Runner"}
        value={value}
        onChange={setValue}
      />
      {kind === "subscription" ? (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          Operator work only
          <InfoTip label="About subscriptions">
            A subscription runner never serves a company: it builds and fixes the platform only.
          </InfoTip>
        </p>
      ) : null}
    </QuickAddDialog>
  );
}

function KeyDialog({ api, agent: a, onClose }: { api: Api; agent: Agent; onClose: () => void }) {
  const saved = useAgentSaved();
  const id = useId();
  const [key, setKey] = useState("");
  const put = useMutation({
    mutationFn: (next: string | null) =>
      api.client.operator.agents.key.put({ params: { slug: a.slug }, body: { key: next } }),
    onSuccess: (next) => {
      saved(next);
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title={`${a.name} API key`}
      info="An Anthropic API key, sealed in the keyring. It is never shown again: only its last four characters."
      submitLabel="Save key"
      pending={put.isPending}
      canSubmit={key.trim().length >= 20}
      onSubmit={() => put.mutate(key.trim())}
      onClose={onClose}
      error={put.error ? <FieldError>{problemText(put.error, "Not saved.")}</FieldError> : null}
      slot="agent-key-dialog"
    >
      {a.has_key ? (
        <div className="flex items-center justify-between gap-2 text-sm" data-slot="key-hint">
          <span>Key ending ••••{a.key_hint}</span>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            disabled={put.isPending}
            onClick={() => put.mutate(null)}
          >
            Remove
          </Button>
        </div>
      ) : null}
      <Field>
        <FieldLabel htmlFor={id}>{a.has_key ? "New key" : "Key"}</FieldLabel>
        <Input
          id={id}
          type="password"
          autoComplete="off"
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
      </Field>
    </QuickAddDialog>
  );
}

function ApisDialog({
  api,
  agent: a,
  apis,
  onClose,
}: {
  api: Api;
  agent: Agent;
  apis: readonly OpApi[];
  onClose: () => void;
}) {
  const saved = useAgentSaved();
  const [chosen, setChosen] = useState<OpApi[]>(a.allowed_apis);
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.agents.put({ params: { slug: a.slug }, body: { allowed_apis: chosen } }),
    onSuccess: (next) => {
      saved(next);
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title={`${a.name} APIs`}
      info="The operator APIs this agent may call. Its job tokens can carry these only."
      submitLabel="Save"
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={save.error ? <FieldError>{problemText(save.error, "Not saved.")}</FieldError> : null}
      slot="agent-apis-dialog"
    >
      <div className="grid gap-2">
        {apis.map((x) => (
          <CheckField
            key={x}
            label={API_LABEL[x]}
            checked={chosen.includes(x)}
            onChange={(on) => setChosen((c) => (on ? [...c, x] : c.filter((y) => y !== x)))}
          />
        ))}
      </div>
    </QuickAddDialog>
  );
}

function CapDialog({ api, agent: a, onClose }: { api: Api; agent: Agent; onClose: () => void }) {
  const saved = useAgentSaved();
  const [cap, setCap] = useState<number | null>(a.daily_cap_micros);
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.agents.put({
        params: { slug: a.slug },
        body: { daily_cap_micros: cap ?? 0 },
      }),
    onSuccess: (next) => {
      saved(next);
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title={`${a.name} daily cap`}
      info="Model spend a day; the agent stops at the cap."
      submitLabel="Save"
      pending={save.isPending}
      canSubmit={cap !== null && Number.isFinite(cap) && cap >= 0}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={save.error ? <FieldError>{problemText(save.error, "Not saved.")}</FieldError> : null}
      slot="agent-cap-dialog"
    >
      <MoneyField label="Daily cap ($)" value={cap} onChange={setCap} />
    </QuickAddDialog>
  );
}

function Tokens({ api, agent: a, onIssue }: { api: Api; agent: Agent; onIssue: () => void }) {
  const saved = useAgentSaved();
  const [target, setTarget] = useState<Agent["tokens"][number] | null>(null);
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api.client.operator.agents.tokens.revoke({ params: { slug: a.slug, id } }),
    onSuccess: (next) => {
      saved(next);
      setTarget(null);
    },
  });
  return (
    <div
      className="flex min-w-0 flex-col gap-2 border-t border-border pt-3"
      data-slot="agent-tokens"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-medium">
          Job tokens
          <InfoTip label="About job tokens">
            A runner signs in with a job token; it may call only the APIs named on it.
          </InfoTip>
        </span>
        <Button size="xs" variant="ghost" onClick={onIssue}>
          <PlusIcon aria-hidden /> Issue token
        </Button>
      </div>
      {a.tokens.length === 0 ? (
        <p className="text-xs text-muted-foreground">No tokens.</p>
      ) : (
        <Table className={DENSE_TABLE}>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Token</TableHead>
              <TableHead scope="col">Expires</TableHead>
              <TableHead scope="col" className="max-sm:hidden">
                Last used
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {a.tokens.map((t) => (
              <TableRow key={t.id} data-token={t.prefix} className="group/row">
                <TableHead scope="row" className="font-normal">
                  <div className="flex flex-wrap items-center gap-1">
                    {t.label}
                    {t.revoked_at ? <Badge variant="outline">Revoked</Badge> : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <code>{t.prefix}…</code> · {t.apis.map((x) => API_LABEL[x]).join(", ")}
                  </div>
                </TableHead>
                <TableCell className="whitespace-nowrap tabular-nums">
                  {formatDate(t.expires_at)}
                </TableCell>
                <TableCell className="whitespace-nowrap max-sm:hidden">
                  {t.last_used_at ? formatDateTime(t.last_used_at) : "Never"}
                </TableCell>
                <TableCell className="w-px text-right">
                  {t.revoked_at ? null : (
                    <RowActions>
                      <RowAction
                        icon={BanIcon}
                        label={`Revoke ${t.label}`}
                        tip="Revoke"
                        onClick={() => setTarget(t)}
                      />
                    </RowActions>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <AlertDialog open={target !== null} onOpenChange={(o) => (o ? null : setTarget(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke “{target?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              A runner using it is refused from now on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revoke.isError ? (
            <FieldError>{problemText(revoke.error, "Not revoked.")}</FieldError>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() => target && revoke.mutate(target.id)}
            >
              {revoke.isPending ? "Revoking…" : "Revoke"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TokenDialog({
  api,
  agent: a,
  apis,
  onClose,
}: {
  api: Api;
  agent: Agent;
  apis: readonly OpApi[];
  onClose: () => void;
}) {
  const saved = useAgentSaved();
  const [label, setLabel] = useState<string | null>("");
  const [chosen, setChosen] = useState<OpApi[]>(
    a.allowed_apis.filter((x) => x.startsWith("jobs.")),
  );
  const [days, setDays] = useState(30);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const create = useMutation({
    mutationFn: () =>
      api.client.operator.agents.tokens.create({
        params: { slug: a.slug },
        body: { label: (label ?? "").trim(), apis: chosen, days },
      }),
    onSuccess: (r) => {
      saved(r.agent);
      setToken(r.token);
    },
  });
  if (token)
    return (
      <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
        <DialogContent data-slot="token-once">
          <DialogHeader>
            <DialogTitle>Token issued</DialogTitle>
          </DialogHeader>
          <Notice tone="warning">Copy it now: it is not shown again.</Notice>
          <div className="flex min-w-0 items-center gap-2">
            <code
              className="min-w-0 flex-1 break-all rounded-md bg-muted px-2 py-1.5 text-xs"
              data-slot="token-value"
            >
              {token}
            </code>
            <Button
              size="icon-sm"
              variant="secondary"
              aria-label="Copy token"
              onClick={() => {
                void navigator.clipboard?.writeText(token).then(() => setCopied(true));
              }}
            >
              {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  return (
    <QuickAddDialog
      title={`New ${a.name} token`}
      info="Scoped to the APIs you tick; shown once."
      submitLabel="Issue token"
      pending={create.isPending}
      canSubmit={(label ?? "").trim().length > 0 && chosen.length > 0 && days >= 1 && days <= 90}
      onSubmit={() => create.mutate()}
      onClose={onClose}
      error={
        create.error ? <FieldError>{problemText(create.error, "Not issued.")}</FieldError> : null
      }
      slot="token-dialog"
    >
      <TextField label="Label" value={label} placeholder="Runner on node 1" onChange={setLabel} />
      <div className="grid gap-2">
        <span className="text-sm font-medium">APIs</span>
        {apis
          .filter((x) => a.allowed_apis.includes(x))
          .map((x) => (
            <CheckField
              key={x}
              label={API_LABEL[x]}
              checked={chosen.includes(x)}
              onChange={(on) => setChosen((c) => (on ? [...c, x] : c.filter((y) => y !== x)))}
            />
          ))}
      </div>
      <NumberField label="Valid for (days)" value={days} min={1} max={90} onChange={setDays} />
    </QuickAddDialog>
  );
}

// Jobs ---------------------------------------------------------------------------------------------

const JOB_KIND: Record<Job["kind"], string> = {
  company_from_pack: "New company",
  apply_baseline: "Apply baseline",
};
const JOB_STATE: Record<
  Job["state"],
  { label: string; variant: "success" | "danger" | "info" | "outline" }
> = {
  queued: { label: "Queued", variant: "outline" },
  running: { label: "Running", variant: "info" },
  done: { label: "Done", variant: "success" },
  failed: { label: "Failed", variant: "danger" },
};
const STEP_STATE: Record<Job["steps"][number]["state"], string> = {
  pending: "Pending",
  done: "Done",
  skipped: "Skipped",
  failed: "Failed",
};

export function JobStateBadge({ state }: { state: Job["state"] }) {
  const s = JOB_STATE[state];
  return (
    <Badge variant={s.variant} data-slot="job-state">
      {s.label}
    </Badge>
  );
}

/** A job's checklist. */
export function JobSteps({ job }: { job: Job }) {
  return (
    <ol className="grid gap-1 text-xs" data-slot="job-steps">
      {job.steps.map((s) => (
        <li key={s.step} className="flex min-w-0 items-baseline gap-2" data-step={s.step}>
          <span
            className={
              s.state === "failed"
                ? "w-16 shrink-0 text-danger-text"
                : s.state === "done"
                  ? "w-16 shrink-0 text-success-text"
                  : "w-16 shrink-0 text-muted-foreground"
            }
          >
            {STEP_STATE[s.state]}
          </span>
          <span className="font-medium">{s.step}</span>
          <span className="min-w-0 break-words text-muted-foreground">{s.detail}</span>
        </li>
      ))}
      {job.last_error ? <li className="text-danger-text">{job.last_error}</li> : null}
    </ol>
  );
}

function Jobs({ api }: { api: Api }) {
  const q = useQuery(jobsQuery(api));
  const qc = useQueryClient();
  const scope = useSettingsScope();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const retry = useMutation({
    mutationFn: (id: string) => api.client.operator.jobs.retry({ params: { id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobsKey }),
  });
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const companyName = (j: Job) => {
    const pack = (j.payload as { pack?: { name?: string } }).pack;
    return (
      scope.companies.find((c) => c.id === j.company_id)?.name ??
      pack?.name ??
      scope.satellites.find((s) => s.id === (j.payload as { satellite_id?: string }).satellite_id)
        ?.name ??
      "—"
    );
  };
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="operator-jobs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Jobs</h2>
        <Button size="sm" onClick={() => setCreating(true)}>
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          New company from pack
        </Button>
      </div>
      {retry.isError ? (
        <FieldError>{problemText(retry.error, "The job was not retried.")}</FieldError>
      ) : null}
      {q.isPending ? (
        <Loading label="Loading jobs" rows={2} />
      ) : q.isError ? (
        <LoadError what="Jobs" error={q.error} />
      ) : q.data.jobs.length === 0 ? (
        <EmptyState variant="card" icon={ListChecksIcon} title="No jobs yet" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="jobs-table">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Job</TableHead>
              <TableHead scope="col">State</TableHead>
              <TableHead scope="col" className="max-sm:hidden">
                For
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Created
              </TableHead>
              <TableHead scope="col" className="text-right">
                Steps
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.jobs.map((j) => {
              const expanded = open.has(j.id);
              const done = j.steps.filter((s) => s.state === "done" || s.state === "skipped");
              return (
                <Fragment key={j.id}>
                  <TableRow data-job={j.kind} data-state={j.state} className="group/row">
                    <TableHead scope="row" className="font-normal">
                      <button
                        type="button"
                        aria-expanded={expanded}
                        className="inline-flex items-center gap-1 hover:underline"
                        onClick={() => toggle(j.id)}
                      >
                        {expanded ? (
                          <ChevronDownIcon aria-hidden="true" className="size-3.5" />
                        ) : (
                          <ChevronRightIcon aria-hidden="true" className="size-3.5" />
                        )}
                        {JOB_KIND[j.kind]}
                      </button>
                    </TableHead>
                    <TableCell>
                      <JobStateBadge state={j.state} />
                    </TableCell>
                    <TableCell className="max-sm:hidden">{companyName(j)}</TableCell>
                    <TableCell className="whitespace-nowrap max-md:hidden">
                      {formatDateTime(j.created_at)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {done.length}/{j.steps.length}
                    </TableCell>
                    <TableCell className="w-px text-right">
                      {j.state === "failed" ? (
                        <RowActions>
                          <RowAction
                            icon={RotateCwIcon}
                            label={`Retry ${JOB_KIND[j.kind].toLowerCase()}`}
                            tip="Retry"
                            disabled={retry.isPending}
                            onClick={() => retry.mutate(j.id)}
                          />
                        </RowActions>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  {expanded ? (
                    <TableRow data-job-steps={j.id}>
                      <TableCell colSpan={6} className="pl-8">
                        <JobSteps job={j} />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
      {creating ? <NewCompanyDialog api={api} onClose={() => setCreating(false)} /> : null}
    </section>
  );
}

const slugOf = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

function NewCompanyDialog({ api, onClose }: { api: Api; onClose: () => void }) {
  const qc = useQueryClient();
  const scope = useSettingsScope();
  const [name, setName] = useState<string | null>("");
  const [slug, setSlug] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("USD");
  const [timezone, setTimezone] = useState<string | null>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [email, setEmail] = useState<string | null>(null);
  const [owner, setOwner] = useState<string | null>(null);
  const [satellite, setSatellite] = useState("");
  const [job, setJob] = useState<Job | null>(null);
  const theSlug = slug ?? slugOf(name ?? "");
  const create = useMutation({
    mutationFn: () =>
      api.client.operator.deployer.companyFromPack({
        body: {
          pack: {
            name: (name ?? "").trim(),
            slug: theSlug,
            reporting_currency: currency as (typeof Currency.options)[number],
            ...(timezone ? { timezone } : {}),
            ...(email && owner
              ? { owner: { email: email.trim(), display_name: owner.trim() } }
              : {}),
            ...(satellite ? { satellite_id: satellite } : {}),
          },
        },
      }),
    onSuccess: (j) => {
      setJob(j);
      void qc.invalidateQueries({ queryKey: jobsKey });
    },
  });
  if (job)
    return (
      <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
        <DialogContent data-slot="new-company-job">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              New company from pack <JobStateBadge state={job.state} />
            </DialogTitle>
          </DialogHeader>
          <JobSteps job={job} />
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  const ownerHalf = Boolean(email) !== Boolean(owner);
  return (
    <QuickAddDialog
      title="New company from pack"
      info="The Deployer creates the company, invites the owner, applies its settings, places it and checks it is healthy, step by step. The plan is set in Billing."
      submitLabel="Start"
      size="lg"
      pending={create.isPending}
      canSubmit={Boolean((name ?? "").trim()) && /^[a-z0-9][a-z0-9-]*$/.test(theSlug) && !ownerHalf}
      onSubmit={() => create.mutate()}
      onClose={onClose}
      error={
        create.error ? <FieldError>{problemText(create.error, "Not started.")}</FieldError> : null
      }
      slot="new-company-dialog"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Name" value={name} onChange={setName} />
        <TextField label="Slug" value={theSlug} onChange={setSlug} />
        <SelectField
          label="Currency"
          value={currency}
          options={Currency.options.map((c) => ({ value: c, label: c }))}
          onChange={setCurrency}
        />
        <TextField label="Timezone" nullable value={timezone} onChange={setTimezone} />
        <TextField
          label="Owner email"
          nullable
          placeholder="Optional"
          value={email}
          onChange={setEmail}
        />
        <TextField
          label="Owner name"
          nullable
          placeholder="Optional"
          value={owner}
          onChange={setOwner}
        />
        <SelectField
          label="Satellite"
          value={satellite}
          options={[
            { value: "", label: "Decide later" },
            ...scope.satellites.map((s) => ({ value: s.id, label: s.name })),
          ]}
          onChange={setSatellite}
        />
      </div>
      {ownerHalf ? (
        <p className="text-xs text-muted-foreground">An owner needs both an email and a name.</p>
      ) : null}
    </QuickAddDialog>
  );
}
